import { computed } from 'vue';
import { BUTTON_TYPES } from '../constants/buttonTypes';

/**
 * Creates button type configuration based on platform limits
 * @param {Object} limits - Platform-specific limits
 * @returns {Object} Button type configuration
 */
export const createButtonTypeConfig = limits => ({
  [BUTTON_TYPES.URL]: {
    max: limits.MAX_URL_BUTTONS ?? limits.CALL_TO_ACTION_MAX_URL_BUTTONS ?? 2,
    mutuallyExclusiveWith: [BUTTON_TYPES.VOICE_CALL_REQUEST],
  },
  [BUTTON_TYPES.PHONE_NUMBER]: {
    max:
      limits.MAX_PHONE_BUTTONS ?? limits.CALL_TO_ACTION_MAX_PHONE_BUTTONS ?? 1,
    mutuallyExclusiveWith: [
      BUTTON_TYPES.VOICE_CALL,
      BUTTON_TYPES.VOICE_CALL_REQUEST,
    ],
  },
  [BUTTON_TYPES.COPY_CODE]: {
    max:
      limits.MAX_COPY_CODE_BUTTONS ??
      limits.CALL_TO_ACTION_MAX_COPY_CODE_BUTTONS ??
      1,
    mutuallyExclusiveWith: [BUTTON_TYPES.VOICE_CALL_REQUEST],
  },
  [BUTTON_TYPES.VOICE_CALL]: {
    max:
      limits.MAX_VOICE_CALL_BUTTONS ??
      limits.CALL_TO_ACTION_MAX_VOICE_CALL_BUTTONS ??
      1,
    mutuallyExclusiveWith: [
      BUTTON_TYPES.PHONE_NUMBER,
      BUTTON_TYPES.VOICE_CALL_REQUEST,
    ],
  },
  [BUTTON_TYPES.VOICE_CALL_REQUEST]: {
    max:
      limits.MAX_VOICE_CALL_REQUEST_BUTTONS ??
      limits.CALL_TO_ACTION_MAX_VOICE_CALL_REQUEST_BUTTONS ??
      1,
    mustBeAlone: true,
  },
});

/**
 * Composable for button type rules and validation
 * Handles button type selection logic and mutual exclusivity
 *
 * @param {Ref<Array>} buttons - Ref to buttons array
 * @param {Ref<Array>} buttonTypeOptions - Ref to available button type options
 * @param {Object} config - Platform configuration
 * @param {Object} config.limits - Platform limits object
 * @param {number} [config.maxButtons=4] - Maximum number of buttons
 * @param {number} [config.minButtons=1] - Minimum number of buttons
 */
export function useButtonTypeRules(buttons, buttonTypeOptions, config = {}) {
  const {
    limits = {},
    maxButtons = limits.CALL_TO_ACTION_MAX_BUTTONS ?? 4,
    minButtons = limits.CALL_TO_ACTION_MIN_BUTTONS ?? 1,
  } = config;

  const BUTTON_TYPE_CONFIG = createButtonTypeConfig(limits);

  /**
   * Count buttons by type, optionally excluding a specific index
   * @param {number|null} excludeIndex - Index to exclude from counting
   * @returns {Object} Counts by type and total
   */
  const countButtonsByType = (excludeIndex = null) => {
    const counts = {
      [BUTTON_TYPES.URL]: 0,
      [BUTTON_TYPES.PHONE_NUMBER]: 0,
      [BUTTON_TYPES.VOICE_CALL]: 0,
      [BUTTON_TYPES.VOICE_CALL_REQUEST]: 0,
      [BUTTON_TYPES.COPY_CODE]: 0,
    };
    let totalOtherButtons = 0;

    buttons.value.forEach((button, index) => {
      if (excludeIndex === null || index !== excludeIndex) {
        totalOtherButtons += 1;
        if (counts[button.type] !== undefined) {
          counts[button.type] += 1;
        }
      }
    });

    return { counts, totalOtherButtons };
  };

  /**
   * Check if a button type can be selected
   * @param {string} type - Button type to check
   * @param {Object} counts - Current counts by type
   * @param {number} totalOtherButtons - Total number of other buttons
   * @returns {boolean} Whether the type can be selected
   */
  const canSelectType = (type, counts, totalOtherButtons) => {
    const typeConfig = BUTTON_TYPE_CONFIG[type];
    if (!typeConfig) return true;

    // Check if we've reached the maximum for this type
    if (counts[type] >= typeConfig.max) {
      return false;
    }

    // Check if this button must be alone
    if (typeConfig.mustBeAlone && totalOtherButtons > 0) {
      return false;
    }

    // Check mutual exclusivity
    if (typeConfig.mutuallyExclusiveWith) {
      const hasExclusiveType = typeConfig.mutuallyExclusiveWith.some(
        exclusiveType => counts[exclusiveType] > 0
      );
      if (hasExclusiveType) {
        return false;
      }
    }

    return true;
  };

  /**
   * Get available button type options for a specific button index
   * Filters out types that cannot be selected based on current state
   * @param {number} index - Button index
   * @returns {Array} Available button type options
   */
  const getButtonTypeOptions = index => {
    const currentButton = buttons.value[index];
    const { counts, totalOtherButtons } = countButtonsByType(index);

    // Filter out options that cannot be selected
    return buttonTypeOptions.value.filter(option => {
      // Current button's type is always available
      if (currentButton && option.value === currentButton.type) {
        return true;
      }

      return canSelectType(option.value, counts, totalOtherButtons);
    });
  };

  /**
   * Count all buttons by type (without exclusion)
   */
  const buttonCounts = computed(() => {
    const { counts } = countButtonsByType();
    return counts;
  });

  /**
   * Get available types for new buttons
   */
  const availableTypesForNewButton = computed(() => {
    const { counts, totalOtherButtons } = countButtonsByType();
    const available = [];

    Object.values(BUTTON_TYPES).forEach(type => {
      // Skip QUICK_REPLY since it's not for CTA
      if (type === BUTTON_TYPES.QUICK_REPLY) return;

      if (canSelectType(type, counts, totalOtherButtons)) {
        available.push(type);
      }
    });

    return available;
  });

  /**
   * Check if we can add more buttons
   */
  const canAddButton = computed(() => {
    // Cannot add more buttons if Voice Call Request exists (it must be alone)
    const hasVoiceCallRequest = buttons.value.some(
      b => b.type === BUTTON_TYPES.VOICE_CALL_REQUEST
    );
    if (hasVoiceCallRequest) return false;

    // Cannot add more buttons if we've reached the max
    if (buttons.value.length >= maxButtons) {
      return false;
    }

    // Check if there's at least one valid type available for a new button
    return availableTypesForNewButton.value.length > 0;
  });

  /**
   * Check if we can remove buttons
   */
  const canRemoveButton = computed(() => {
    return buttons.value.length > minButtons;
  });

  /**
   * Get the first valid button type for a new button
   * Returns null if no valid type is available
   * @returns {string|null} First available button type or null
   */
  const getFirstAvailableType = () => {
    // First try to get from buttonTypeOptions
    const { counts, totalOtherButtons } = countButtonsByType();

    const availableOption = buttonTypeOptions.value.find(option =>
      canSelectType(option.value, counts, totalOtherButtons)
    );

    if (availableOption) {
      return availableOption.value;
    }

    return availableTypesForNewButton.value[0] || null;
  };

  return {
    getButtonTypeOptions,
    buttonCounts,
    canAddButton,
    canRemoveButton,
    getFirstAvailableType,
    availableTypesForNewButton,
    BUTTON_TYPE_CONFIG,
  };
}
